<template>
  <div class="small-form-grid">
    <div class="grid-x grid-padding-x">
      <TheIntegerInput
        v-model.trim="form.Spadenr"
        :label="spade.Spadeid ? 'Spadenr' : 'Spadenr (ny)'"
        containerClassName="cell auto"
        name="Spadenr"
        maxlength="4"
        required
        v-validate="rulesSpadenr"
        :hideErrorMsg="true"
      />

      <TheAutoCompleteInput
        v-model.trim="customer.parsed_name"
        containerClassName="cell small-12"
        name="Kunde"
        :placeholder="'Søk kunde…'"
        :label="'Kunde'"
        :onInputCallback="onKundeSearchInput"
        :onSelectCallback="onKundeSearchSelected"
        :onResetCallback="onKundeSearchReset"
        :debounce="1250"
        :minquery="2"
        :hideErrorMsg="true"
      >
        <label slot="label">
          Kunde * (<a href="javascript:;" @click="showCustomerModal()">Ny kunde</a>)
        </label>
        <div slot="item" slot-scope="props">
          <strong>{{props.item.Kundenr}}</strong>
          <span v-if="props.item.Bedrift">{{ props.item.FirmaNavn || props.item.full_name }}</span>
          <span v-else>{{ props.item.full_name }}</span>
        </div>
      </TheAutoCompleteInput>

      <div class="cell large-12">
        <input type="hidden" name="Kundenr" v-model="customer.Kundenr" v-validate="rulesCustomer"/>
        <dl class="small-box horizontal" v-if="customer.Kundenr">
          <dt>Kundenr</dt>
          <dd>{{customer.Kundenr}}</dd>

          <dt>Navn</dt>
          <dd>{{customer.parsed_name}}</dd>

          <dt>Mobil</dt>
          <dd>{{customer.Mobil || '-'}}</dd>

          <dt>Tlf. privat</dt>
          <dd>{{customer.Tlfprivat || '-'}}</dd>

          <dt>Tlf. arbeid</dt>
          <dd>{{customer.Tlfarbeid || '-'}}</dd>
        </dl>
      </div>

      <div class="cell large-12" v-if="errors.all().length > 0">
        <div class="panel small error">
          <ul>
            <li v-if="errors.has('Spadenr')">
              <span class="icon"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
              {{errors.first('Spadenr')}}
            </li>
            <li v-if="errors.has('Auksjon')">
              <span class="icon"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
              {{errors.first('Auksjon')}}
            </li>
            <li v-if="errors.has('Kundenr')">
              <span class="icon"><svg width="16px" height="16px" viewBox="0 0 16 16"><use xlink:href="/static/sprites.svg#button-glyph-error"></use></svg></span>
              Du må velge en kunde...
            </li>
          </ul>
        </div>
      </div>

      <div class="cell auto">
        <!-- Leave empty or have the prev/next button here -->
      </div>

      <template v-if="loading">
        <div class="cell shrink">
          <button type="submit" class="button small primary disabled">Lagre</button>
        </div>
      </template>
      <template v-else-if="changed">
        <div class="cell shrink">
          <button v-if="spade.Spadeid && !deleting" @click="removeConfirm" type="button" class="button small primary transparent">Slett</button>

          <button v-if="!saving" @click="handleSubmit" type="submit" class="button small primary">Lagre</button>
          <button v-else type="submit" class="button small primary loading">Lagre <span class="loader-spinner"></span></button>
        </div>
      </template>
      <template v-else>
        <div class="cell shrink">
          <button v-if="spade.Spadeid && !deleting" @click="removeConfirm" type="button" class="button small primary transparent">Slett</button>

          <button type="submit" class="button small primary disabled">Lagre</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import bus from '@/plugins/event-bus'
import KundeService from '@/services/KundeService'
import AuksjonService from '../../../../services/AuksjonService'
import TheAutoCompleteInput from '../../../Inputs/TheAutoCompleteInput'

export default {
  name: 'TheSidebarModule',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    TheAutoCompleteInput
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    used: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      saving: false,
      deleting: false,
      spade: {},
      bid: {},
      form: {},
      orig: {},
      customer: {}
    }
  },
  computed: {
    changed: function () {
      return !this._.isEqual(this.form, this.orig)
    },
    rulesSpadenr: function () {
      return {
        required: true,
        SpadenrValidation: true
      }
    },
    rulesCustomer: function () {
      return {
        required: true,
        min: 1
      }
    }
  },
  watch: {
    item: function (newVal) {
      this.init()
    }
  },
  created () {
    bus.$off('kunde_form_saved')

    this.$validator.extend('SpadenrValidation', {
      validate: this.spadenrValidation,
      getMessage: (field, params, data) => data ? data.message : 'Spaden er allerede registrert'
    })
  },
  mounted () {
    bus.$on('kunde_form_saved', (data) => this.onKundeCreated(data))

    this.init()
  },
  methods: {
    revalidateCustomer: function () {
      const vm = this
      setTimeout(function () {
        vm.$validator.validate('Kundenr')
      })
    },
    spadenrValidation: function (value) {
      return new Promise(resolve => {
        if (value === this.orig.Spadenr) {
          // Same as original: OK
          return resolve({ valid: true })
        }

        if (this.used.includes(Number(value))) {
          return resolve({ valid: false, data: { message: 'Spaden er allerede registrert' } })
        }

        // Min / max validation
        const v = Number(value || 0)
        if (!((v > 0 && v < 400) || (v >= 950 && v <= 952) || (v >= 1000 && v <= 2000))) {
          // Ok
          return resolve({ valid: false, data: { message: 'Spadenr må være mindre enn 400, eller mellom 950-952, eller mellom 1000-2000' } })
        }

        resolve({ valid: true })
      })
    },
    showCustomerModal: function () {
      this.openModal('kunde-card-create-modal')
    },
    onKundeSearchInput: function (query) {
      return KundeService.search(query)
    },
    onKundeCreated: function (data) {
      this.customer = data
      this.form.Kundenr = data.Kundenr
      this.closeModal()
      this.revalidateCustomer()
    },
    onKundeSearchReset: function () {
      this.customer = {}
      this.form.Kundenr = ''
      this.revalidateCustomer()
    },
    onKundeSearchSelected: function (item) {
      this.customer = item || {}
      this.form.Kundenr = String(item.Kundenr || '')
      this.revalidateCustomer()
    },
    init: function () {
      // Set spade
      this.spade = this._.cloneDeep(this.item.obj || {})

      // Make original and form
      this.orig = {
        Spadenr: String(this.spade.Spadenr || ''),
        Kundenr: String(this.spade.Kundenr || ''),
        Auksjon: String(this.spade.Auksjon),
        Avdelingsid: 1
      }

      this.form = this._.cloneDeep(this.orig)

      if (this.spade.kunde) {
        this.customer = this._.cloneDeep(this.spade.kunde)
      } else {
        this.customer = {}
      }

      // Clear error bag
      this.errors.clear()
    },
    handleSubmit: function () {
      if (this.saving || !this.changed) {
        return
      }

      this.$validator.validateAll()
        .then((result) => {
          if (!result) {
          } else if (this.spade.Spadeid) {
            this.update()
          } else {
            this.create()
          }
        })
    },
    create: function () {
      this.saving = true

      AuksjonService.createSpade(this.form)
        .then(response => {
          const data = Object.assign({}, response.data, { _action: 'create' })
          this.$emit('on-save', data)
        })
        .catch(error => {
          console.log('Error', error)
          this.$helpers.sendApiErrorsToValidator(error, this.errors)

          // Check if new errors appeared
          if (this.errors.any()) {
            this.$validator.resume()
            this.$helpers.showErrorAlert('Kunne ikke lagre endringer', 'Vennligst korriger feil i skjemaet')
          } else {
            this.$helpers.showErrorAlert('Feil', 'Noe gikk feil i lagringen.')
          }
        })
        .then(() => {
          this.saving = false
        })
    },
    update: function () {
      this.saving = true

      AuksjonService.updateSpade(this.spade.Spadeid, this.form)
        .then(response => {
          const data = Object.assign({}, response.data, { _action: 'update' })
          this.$emit('on-save', data)
        })
        .catch(error => {
          console.log('Error', error)
          this.$helpers.sendApiErrorsToValidator(error, this.errors)

          // Check if new errors appeared
          if (this.errors.any()) {
            this.$validator.resume()
            this.$helpers.showErrorAlert('Kunne ikke lagre endringer', 'Vennligst korriger feil i skjemaet')
          } else {
            this.$helpers.showErrorAlert('Feil', 'Noe gikk feil i lagringen.')
          }
        })
        .then(() => {
          this.saving = false
        })
    },
    removeConfirm: function () {
      const vm = this
      this.openConfirm(
        'Ønsker du å slette dette spadenr?',
        'Denne handlingen kan ikke angres.',
        'Ja',
        'Nei',
        function () {
          vm.closeModal()
          vm.remove()
        },
        function () {
          vm.closeModal()
        }
      )
    },
    remove: function () {
      this.deleting = true

      AuksjonService.deleteSpade(this.spade.Spadeid)
        .then(response => {
          this.$emit('on-delete', { Spadeid: this.spade.Spadeid })
        })
        .catch(error => {
          console.log('Error', error)
          this.$helpers.showErrorAlert('Feil', 'Noe gikk feil i lagringen.')
        })
        .then(() => {
          this.deleting = false
        })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Admin/Auctions/Spader/TheSidebarModule.vue